import React, { useState, useEffect, useCallback } from "react";
import { Table, Input, Tag, Dropdown, Select, Modal, message, Spin, Switch, Tooltip, Flex, Row, Col, Card, Badge, Button } from "antd";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  MoreOutlined, 
  SearchOutlined, 
  FilePdfOutlined,
  VideoCameraOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  StopOutlined,
  TeamOutlined,
  CalendarOutlined,
  BarsOutlined,
  AppstoreOutlined,
  LoadingOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { PageHeader, SubmittedProfilesWrapper } from "./SubmittedProfiles.style";
import { API_CONST } from "../../../const";

const StatCard = styled(Card)`
  border-radius: 8px;
  .stat-inner { display: flex; align-items: center; gap: 14px; }
  .stat-icon {
    width: 44px; height: 44px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0;
  }
  .stat-info h3 { margin: 0; font-size: 22px; font-weight: 700; color: #014c75; line-height: 1.2; }
  .stat-info p { margin: 0; font-size: 12px; color: #888; }
`;

const KanbanBoard = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding-bottom: 16px;
  align-items: flex-start;
  min-height: 200px;
`;

const KanbanColumn = styled.div`
  flex: 0 0 270px;
  background: #f7f8fa;
  border-radius: 10px;
  border-top: 3px solid ${(p) => p.$color};
  padding: 12px;
  min-height: 160px;
`;

const KanbanColHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  .col-title {
    font-size: 13px;
    font-weight: 600;
    color: ${(p) => p.$color};
  }
`;

const KanbanCard = styled.div`
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e8eaf0;
  padding: 12px;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  transition: box-shadow 0.15s;
  &:hover { box-shadow: 0 3px 10px rgba(0,0,0,0.09); }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 10px;
  gap: 6px;
`;

const CardName = styled.a`
  font-size: 13px;
  font-weight: 600;
  color: #1890ff !important;
  line-height: 1.3;
  flex: 1;
  &:hover { text-decoration: underline; }
`;

const CardField = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 5px;
  font-size: 12px;
  .field-label { color: #aaa; min-width: 68px; flex-shrink: 0; }
  .field-value { color: #3d3d3d; font-weight: 500; word-break: break-word; }
`;

const ViewToggleBtn = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
  cursor: pointer;
  background: ${(p) => (p.$active ? "#014c75" : "#fff")};
  border-color: ${(p) => (p.$active ? "#014c75" : "#d9d9d9")};
  color: ${(p) => (p.$active ? "#fff" : "#595959")};
  font-size: 16px;
  transition: all 0.2s;
  &:hover {
    border-color: #014c75;
    color: ${(p) => (p.$active ? "#fff" : "#014c75")};
  }
`;

const { Search } = Input;
const { Option } = Select;

const KANBAN_COLUMNS = [
  { key: "Under Review",        label: "Under Review",        color: "#1677ff" },
  { key: "Interview Scheduled", label: "Interview Scheduled", color: "#fa8c16" },
  { key: "Hired",               label: "Hired",               color: "#52c41a" },
  { key: "Rejected",            label: "Rejected",            color: "#ff4d4f" },
];

const formatCurrency = (amount) => {
  if (!amount) return "-";
  return `₹ ${Number(amount).toLocaleString('en-IN')}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: '2-digit' });
};

const mapStatusToDisplay = (status) => {
  const statusMap = {
    'submitted': 'Under Review',
    'interview_requested': 'Under Review',
    'interview_scheduled': 'Interview Scheduled',
    'hired': 'Hired',
    'rejected': 'Rejected',
  };
  return statusMap[status] || status;
};

const SubmittedProfiles = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [selectedJob, setSelectedJob] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobOptions, setJobOptions] = useState([]);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [resumeLoadingId, setResumeLoadingId] = useState(null);
  const [myPartnersOnly, setMyPartnersOnly] = useState(
    () => sessionStorage.getItem("submittedProfiles_myPartnersOnly") === "true"
  );
  const [activePartnerOrgs, setActivePartnerOrgs] = useState([]);
  const [partnerOrgsLoading, setPartnerOrgsLoading] = useState(false);
  const [viewMode, setViewMode] = useState("kanban");

  const fetchActivePartnerOrgs = useCallback(async () => {
    setPartnerOrgsLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(API_CONST.PARTNER_RELATION_MY_PARTNERS, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const relations = response.data.relations || [];
      const orgs = relations
        .filter((r) => r.status === "active" && r.partnerOrganization)
        .map((r) => r.partnerOrganization);
      setActivePartnerOrgs(orgs);
      return orgs;
    } catch (error) {
      console.error("Error fetching partner orgs:", error);
      message.error("Failed to load partner organizations");
      return [];
    } finally {
      setPartnerOrgsLoading(false);
    }
  }, []);

  const fetchAllApplications = useCallback(async (partnerOrgs = []) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      
      const jobsResponse = await axios.post(API_CONST.GET_JOB_REQUIREMENTS, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const jobs = jobsResponse.data.Response || [];
      const activeJobs = jobs.filter(job => job.status === 'open' || job.status === 'draft');
      
      setJobOptions(activeJobs.map(job => ({ id: job.id, title: job.role })));
      
      const allApplications = [];
      for (const job of activeJobs) {
        try {
          const appBody = { jobId: job.id };
          if (partnerOrgs.length > 0) {
            appBody.partnerOrganizations = partnerOrgs;
          }
          const appResponse = await axios.post(
            API_CONST.GET_JOB_APPLICATIONS,
            appBody,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          const applications = appResponse.data.applications || [];
          applications.forEach(app => {
            allApplications.push({
              key: app.applicationId,
              applicationId: app.applicationId,
              talentId: app.talentId,
              name: app.talent?.name || 'Unknown',
              jobId: job.id,
              jobTitle: job.role,
              experience: app.talent?.experience || '-',
              primarySkill: (app.talent?.primarySkills || []).filter(Boolean).join(', ') || '-',
              monthlyCost: formatCurrency(app.talent?.monthlyRate),
              submittedOn: formatDate(app.submittedAt),
              status: mapStatusToDisplay(app.status),
              rawStatus: app.status,
              resumeUrl: app.talent?.resumeUrl,
              partnerOrganization: app.partnerOrganization || null,
            });
          });
        } catch (error) {
          console.error(`Error fetching applications for job ${job.id}:`, error);
        }
      }
      
      setProfiles(allApplications);
    } catch (error) {
      console.error("Error fetching applications:", error);
      message.error("Failed to load submitted profiles");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleMyPartnersToggle = async (checked) => {
    setMyPartnersOnly(checked);
    sessionStorage.setItem("submittedProfiles_myPartnersOnly", String(checked));
    if (checked) {
      if (activePartnerOrgs.length === 0) {
        const orgs = await fetchActivePartnerOrgs();
        if (orgs.length === 0) {
          setProfiles([]);
          setLoading(false);
        } else {
          fetchAllApplications(orgs);
        }
      } else {
        fetchAllApplications(activePartnerOrgs);
      }
    } else {
      fetchAllApplications([]);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (sessionStorage.getItem("submittedProfiles_myPartnersOnly") === "true") {
        const orgs = await fetchActivePartnerOrgs();
        if (orgs.length === 0) {
          setProfiles([]);
          setLoading(false);
        } else {
          fetchAllApplications(orgs);
        }
      } else {
        fetchAllApplications([]);
      }
    };
    init();
  }, [fetchActivePartnerOrgs, fetchAllApplications]);

  const handleScheduleInterview = (record) => {
    Modal.confirm({
      title: "Schedule Interview",
      icon: <VideoCameraOutlined style={{ color: "#1890ff" }} />,
      content: (
        <div>
          <p>Are you sure you want to schedule an interview for this candidate?</p>
          <div style={{ marginTop: 12, padding: 12, background: "#f8f9fd", borderRadius: 6 }}>
            <p style={{ margin: 0, fontWeight: 500 }}>{record.name}</p>
            <p style={{ margin: "4px 0 0", color: "#666", fontSize: 13 }}>{record.jobTitle}</p>
          </div>
        </div>
      ),
      okText: "Schedule Interview",
      okButtonProps: { style: { background: "#00d9a9", borderColor: "#00d9a9" } },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.put(
            `${API_CONST.UPDATE_JOB_APPLICATION}/${record.applicationId}`,
            { status: "interview_scheduled" },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setProfiles(
            profiles.map((p) =>
              p.key === record.key 
                ? { ...p, previousStatus: p.rawStatus, status: "Interview Scheduled", rawStatus: "interview_scheduled" } 
                : p
            )
          );
          message.success(`Interview scheduled for ${record.name}`);
        } catch (error) {
          console.error("Error scheduling interview:", error);
          message.error("Failed to schedule interview");
        }
      },
    });
  };

  const handleCancelInterview = (record) => {
    setSelectedProfile(record);
    setCancelReason("");
    setCancelModalVisible(true);
  };

  const handleSubmitCancelInterview = async () => {
    if (!cancelReason.trim()) {
      message.warning("Please provide a reason for cancelling the interview");
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      await axios.put(
        `${API_CONST.UPDATE_JOB_APPLICATION}/${selectedProfile.applicationId}`,
        { status: "submitted", notes: `Interview cancelled: ${cancelReason}` },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfiles(
        profiles.map((p) =>
          p.key === selectedProfile.key 
            ? { ...p, status: "Under Review", rawStatus: "submitted", previousStatus: undefined } 
            : p
        )
      );
      message.success(`Interview cancelled for ${selectedProfile.name}`);
      setCancelModalVisible(false);
      setSelectedProfile(null);
      setCancelReason("");
    } catch (error) {
      console.error("Error cancelling interview:", error);
      message.error("Failed to cancel interview");
    }
  };

  const [hireModalVisible, setHireModalVisible] = useState(false);
  const [hireRecord, setHireRecord] = useState(null);
  const [hireNotes, setHireNotes] = useState("");
  const [hireLoading, setHireLoading] = useState(false);

  const handleHire = (record) => {
    setHireRecord(record);
    setHireNotes("");
    setHireModalVisible(true);
  };

  const handleHireConfirm = async () => {
    if (!hireRecord) return;
    setHireLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      await axios.put(
        `${API_CONST.UPDATE_JOB_APPLICATION}/${hireRecord.applicationId}`,
        { status: "hired", ...(hireNotes.trim() ? { notes: hireNotes.trim() } : {}) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfiles(
        profiles.map((p) =>
          p.key === hireRecord.key ? { ...p, status: "Hired", rawStatus: "hired" } : p
        )
      );
      message.success(`${hireRecord.name} has been hired successfully`);
      setHireModalVisible(false);
      setHireRecord(null);
      setHireNotes("");
    } catch (error) {
      console.error("Error hiring candidate:", error);
      message.error("Failed to hire candidate");
    } finally {
      setHireLoading(false);
    }
  };

  const handleReject = (record) => {
    Modal.confirm({
      title: "Reject Candidate",
      icon: <CloseCircleOutlined style={{ color: "#ff4d4f" }} />,
      content: (
        <div>
          <p>Are you sure you want to reject this candidate?</p>
          <div style={{ marginTop: 12, padding: 12, background: "#fff2f0", borderRadius: 6, border: "1px solid #ffccc7" }}>
            <p style={{ margin: 0, fontWeight: 500 }}>{record.name}</p>
            <p style={{ margin: "4px 0 0", color: "#666", fontSize: 13 }}>{record.jobTitle}</p>
          </div>
        </div>
      ),
      okText: "Reject",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.put(
            `${API_CONST.UPDATE_JOB_APPLICATION}/${record.applicationId}`,
            { status: "rejected" },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setProfiles(
            profiles.map((p) =>
              p.key === record.key ? { ...p, status: "Rejected", rawStatus: "rejected" } : p
            )
          );
          message.success(`${record.name} has been rejected`);
        } catch (error) {
          console.error("Error rejecting candidate:", error);
          message.error("Failed to reject candidate");
        }
      },
    });
  };

  const handleViewResume = async (record) => {
    if (!record.talentId) {
      message.warning("No talent information available for this profile");
      return;
    }
    setResumeLoadingId(record.talentId);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_CONST.BRANDED_RESUME_BASE}/${record.talentId}/branded-resume`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.downloadUrl) {
        window.open(response.data.downloadUrl, "_blank");
      } else {
        message.error("Could not get resume download URL");
      }
    } catch (error) {
      console.error("Error downloading branded resume:", error);
      message.error("Failed to download resume. Please try again.");
    } finally {
      setResumeLoadingId(null);
    }
  };

  const getActionMenuItems = (record) => {
    const items = [];

    const isResumeLoading = resumeLoadingId === record.talentId;
    items.push({
      key: "resume",
      label: isResumeLoading ? "Downloading…" : "Download Resume",
      icon: isResumeLoading ? <LoadingOutlined /> : <FilePdfOutlined />,
      disabled: isResumeLoading,
      onClick: () => !isResumeLoading && handleViewResume(record),
    });

    if (record.status === "Under Review") {
      items.push(
        { type: "divider" },
        {
          key: "interview",
          label: "Schedule Interview",
          icon: <VideoCameraOutlined />,
          onClick: () => handleScheduleInterview(record),
        },
        {
          key: "hire",
          label: "Hire",
          icon: <CheckCircleOutlined />,
          onClick: () => handleHire(record),
        },
        {
          key: "reject",
          label: "Reject",
          icon: <CloseCircleOutlined />,
          danger: true,
          onClick: () => handleReject(record),
        }
      );
    } else if (record.status === "Interview Scheduled") {
      items.push(
        { type: "divider" },
        {
          key: "cancel",
          label: "Cancel Interview",
          icon: <StopOutlined />,
          danger: true,
          onClick: () => handleCancelInterview(record),
        },
        {
          key: "hire",
          label: "Hire",
          icon: <CheckCircleOutlined />,
          onClick: () => handleHire(record),
        },
        {
          key: "reject",
          label: "Reject",
          icon: <CloseCircleOutlined />,
          danger: true,
          onClick: () => handleReject(record),
        }
      );
    }

    return items;
  };

  const columns = [
    { 
      title: "Name", 
      dataIndex: "name", 
      key: "name", 
      width: 150,
      render: (text, record) => (
        <a 
          href="#" 
          style={{ color: "#1890ff", fontWeight: 500 }}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/customer/talent-details/${record.talentId}`, {
              state: { from: "/customer/submitted-profiles", fromLabel: "Back to Submitted Profiles" }
            });
          }}
        >
          {text}
        </a>
      ),
    },
    { 
      title: "Job ID", 
      dataIndex: "jobId", 
      key: "jobId", 
      width: 100,
      render: (jobId) => (
        <a 
          href="#" 
          style={{ color: "#1890ff", fontWeight: 500 }}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/customer/view-job/${jobId}`, {
              state: { from: "/customer/submitted-profiles", fromLabel: "Back to Submitted Profiles" }
            });
          }}
        >
          {jobId}
        </a>
      ),
    },
    { 
      title: "Job Title", 
      dataIndex: "jobTitle", 
      key: "jobTitle", 
      width: 200,
      render: (text, record) => (
        <a 
          href="#" 
          style={{ color: "#1890ff" }}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/customer/view-job/${record.jobId}`, {
              state: { from: "/customer/submitted-profiles", fromLabel: "Back to Submitted Profiles" }
            });
          }}
        >
          {text}
        </a>
      ),
    },
    { title: "Experience", dataIndex: "experience", key: "experience", width: 120 },
    { title: "Primary Skill", dataIndex: "primarySkill", key: "primarySkill", width: 220, ellipsis: true },
    { title: "Monthly Cost", dataIndex: "monthlyCost", key: "monthlyCost", width: 140 },
    { title: "Submitted On", dataIndex: "submittedOn", key: "submittedOn", width: 120 },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 160,
      render: (status) => {
        let color = "default";
        if (status === "Under Review") color = "blue";
        if (status === "Interview Scheduled") color = "orange";
        if (status === "Hired") color = "green";
        if (status === "Rejected") color = "red";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Dropdown
          menu={{ items: getActionMenuItems(record) }}
          trigger={["click"]}
        >
          <MoreOutlined style={{ cursor: "pointer", fontSize: 18 }} />
        </Dropdown>
      ),
    },
  ];

  const filteredData = profiles.filter((profile) => {
    const matchesSearch =
      profile.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      profile.profileId?.toLowerCase().includes(searchText.toLowerCase()) ||
      profile.jobTitle?.toLowerCase().includes(searchText.toLowerCase());

    const matchesJob = selectedJob === "all" || profile.jobId === selectedJob;
    const matchesStatus = selectedStatus === "all" || profile.status === selectedStatus;

    return matchesSearch && matchesJob && matchesStatus;
  });

  const stats = {
    total: profiles.length,
    review: profiles.filter((p) => p.rawStatus === "submitted").length,
    interview: profiles.filter((p) => p.rawStatus === "interview_scheduled").length,
    hired: profiles.filter((p) => p.rawStatus === "hired").length,
    rejected: profiles.filter((p) => p.rawStatus === "rejected").length,
  };

  const statCards = [
    { key: "total",     label: "Total Submitted",    value: stats.total,     bg: "#e6f7ff", iconColor: "#1890ff", icon: <TeamOutlined /> },
    { key: "review",    label: "Under Review",        value: stats.review,    bg: "#e6f7ff", iconColor: "#1890ff", icon: <TeamOutlined /> },
    { key: "interview", label: "Interview Scheduled", value: stats.interview, bg: "#fff7e6", iconColor: "#fa8c16", icon: <CalendarOutlined /> },
    { key: "hired",     label: "Hired",               value: stats.hired,     bg: "#f6ffed", iconColor: "#52c41a", icon: <CheckCircleOutlined /> },
    { key: "rejected",  label: "Rejected",            value: stats.rejected,  bg: "#fff1f0", iconColor: "#ff4d4f", icon: <CloseCircleOutlined /> },
  ];

  const renderKanban = () => (
    <KanbanBoard>
      {KANBAN_COLUMNS.map((col) => {
        const cards = filteredData.filter((p) => p.status === col.key);
        return (
          <KanbanColumn key={col.key} $color={col.color}>
            <KanbanColHeader $color={col.color}>
              <span className="col-title">{col.label}</span>
              <Badge
                count={cards.length}
                style={{ backgroundColor: col.color, fontSize: 11, boxShadow: "none" }}
                showZero
              />
            </KanbanColHeader>
            {cards.length === 0 && (
              <div style={{ textAlign: "center", color: "#bbb", fontSize: 12, padding: "24px 0" }}>
                No profiles
              </div>
            )}
            {cards.map((record) => (
              <KanbanCard key={record.key}>
                <CardHeader>
                  <CardName
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/customer/talent-details/${record.talentId}`, {
                        state: { from: "/customer/submitted-profiles", fromLabel: "Back to Submitted Profiles" },
                      });
                    }}
                  >
                    {record.name}
                  </CardName>
                  <Dropdown menu={{ items: getActionMenuItems(record) }} trigger={["click"]}>
                    <MoreOutlined
                      style={{ cursor: "pointer", fontSize: 16, color: "#888", flexShrink: 0, padding: "2px 4px" }}
                    />
                  </Dropdown>
                </CardHeader>
                <CardField>
                  <span className="field-label">Job ID</span>
                  <span className="field-value">
                    <a
                      href="#"
                      style={{ color: "#1890ff" }}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/customer/view-job/${record.jobId}`, {
                          state: { from: "/customer/submitted-profiles", fromLabel: "Back to Submitted Profiles" },
                        });
                      }}
                    >
                      {record.jobId}
                    </a>
                  </span>
                </CardField>
                <CardField>
                  <span className="field-label">Job Title</span>
                  <span className="field-value">{record.jobTitle}</span>
                </CardField>
                <CardField>
                  <span className="field-label">Experience</span>
                  <span className="field-value">{record.experience}</span>
                </CardField>
                <CardField>
                  <span className="field-label">Primary Skill</span>
                  <span className="field-value">{record.primarySkill}</span>
                </CardField>
                <CardField>
                  <span className="field-label">Monthly Cost</span>
                  <span className="field-value">{record.monthlyCost}</span>
                </CardField>
                <CardField>
                  <span className="field-label">Submitted</span>
                  <span className="field-value">{record.submittedOn}</span>
                </CardField>
                <div style={{ marginTop: 8 }}>
                  <Button
                    size="small"
                    block
                    icon={resumeLoadingId === record.talentId ? <LoadingOutlined /> : <DownloadOutlined />}
                    loading={resumeLoadingId === record.talentId}
                    onClick={() => handleViewResume(record)}
                    style={{ fontSize: 12 }}
                  >
                    Download Resume
                  </Button>
                </div>
              </KanbanCard>
            ))}
          </KanbanColumn>
        );
      })}
    </KanbanBoard>
  );

  return (
    <SubmittedProfilesWrapper>
      <PageHeader>
        <h1>Submitted Profiles</h1>
        <p>Review talent profiles submitted for your requirements</p>
      </PageHeader>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {statCards.map((card) => (
          <Col xs={24} sm={12} md={8} lg={4} xl={4} key={card.key} style={{ minWidth: 180 }}>
            <StatCard bodyStyle={{ padding: "16px 20px" }}>
              <div className="stat-inner">
                <div className="stat-icon" style={{ background: card.bg, color: card.iconColor }}>
                  {card.icon}
                </div>
                <div className="stat-info">
                  <h3>{card.value}</h3>
                  <p>{card.label}</p>
                </div>
              </div>
            </StatCard>
          </Col>
        ))}
      </Row>

      <Flex gap={12} style={{ marginBottom: "24px", flexWrap: "wrap", alignItems: "center" }}>
        <Search
          placeholder="Search by name, profile ID, or job title"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: "100%", maxWidth: "300px" }}
          allowClear
        />

        <Select
          placeholder="Filter by Job"
          value={selectedJob}
          onChange={setSelectedJob}
          style={{ width: 250 }}
        >
          <Option value="all">All Jobs</Option>
          {jobOptions.map(job => (
            <Option key={job.id} value={job.id}>{job.id} - {job.title}</Option>
          ))}
        </Select>

        <Select
          placeholder="Filter by Status"
          value={selectedStatus}
          onChange={setSelectedStatus}
          style={{ width: 200 }}
        >
          <Option value="all">All Status</Option>
          <Option value="Under Review">Under Review</Option>
          <Option value="Interview Scheduled">Interview Scheduled</Option>
          <Option value="Hired">Hired</Option>
          <Option value="Rejected">Rejected</Option>
        </Select>

        <Tooltip title="Show only profiles submitted by your registered partner network">
          <Flex align="center" gap={8} style={{
            background: myPartnersOnly ? "#e6f7ff" : "#fafafa",
            border: `1px solid ${myPartnersOnly ? "#91d5ff" : "#d9d9d9"}`,
            borderRadius: 6,
            padding: "4px 12px",
            cursor: "pointer",
          }}>
            <TeamOutlined style={{ color: myPartnersOnly ? "#1890ff" : "#999" }} />
            <span style={{ fontSize: 13, color: myPartnersOnly ? "#1890ff" : "#595959", userSelect: "none" }}>
              My Partners Only
            </span>
            <Switch
              size="small"
              checked={myPartnersOnly}
              onChange={handleMyPartnersToggle}
              loading={partnerOrgsLoading}
            />
          </Flex>
        </Tooltip>

        <span style={{ color: "#888", fontSize: 13, marginLeft: "auto" }}>
          {filteredData.length} profile{filteredData.length !== 1 ? "s" : ""}
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          <ViewToggleBtn
            $active={viewMode === "table"}
            onClick={() => setViewMode("table")}
            title="Table view"
          >
            <BarsOutlined />
          </ViewToggleBtn>
          <ViewToggleBtn
            $active={viewMode === "kanban"}
            onClick={() => setViewMode("kanban")}
            title="Kanban view"
          >
            <AppstoreOutlined />
          </ViewToggleBtn>
        </div>
      </Flex>

      <Spin spinning={loading}>
        {viewMode === "table" ? (
          <Table
            columns={columns}
            dataSource={filteredData}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows`,
            }}
            scroll={{ x: 1250 }}
            locale={{ emptyText: loading ? 'Loading...' : 'No submitted profiles found' }}
          />
        ) : (
          renderKanban()
        )}
      </Spin>

      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 18 }} />
            <span>Hire Candidate</span>
          </div>
        }
        open={hireModalVisible}
        onOk={handleHireConfirm}
        onCancel={() => { setHireModalVisible(false); setHireRecord(null); setHireNotes(""); }}
        okText="Confirm Hire"
        okButtonProps={{ style: { background: "#52c41a", borderColor: "#52c41a" }, loading: hireLoading }}
        cancelText="Cancel"
        width={480}
      >
        {hireRecord && (
          <div>
            <div style={{ marginBottom: 16, padding: 12, background: "#f6ffed", borderRadius: 6, border: "1px solid #b7eb8f" }}>
              <p style={{ margin: 0, fontWeight: 600 }}>{hireRecord.name}</p>
              <p style={{ margin: "4px 0 0", color: "#666", fontSize: 13 }}>{hireRecord.jobTitle}</p>
              {hireRecord.monthlyCost && (
                <p style={{ margin: "4px 0 0", color: "#52c41a", fontSize: 13 }}>Monthly Cost: {hireRecord.monthlyCost}/month</p>
              )}
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 6, color: "#014c75", fontWeight: 500 }}>
                Hiring Notes <span style={{ color: "#999", fontWeight: 400 }}>(optional — visible to Admin and Partner)</span>
              </label>
              <Input.TextArea
                rows={4}
                placeholder="Add any notes about this hire (e.g. start date preference, special requirements)..."
                value={hireNotes}
                onChange={(e) => setHireNotes(e.target.value)}
                maxLength={1000}
                showCount
              />
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="Cancel Interview"
        open={cancelModalVisible}
        onOk={handleSubmitCancelInterview}
        onCancel={() => {
          setCancelModalVisible(false);
          setSelectedProfile(null);
          setCancelReason("");
        }}
        okText="Cancel Interview"
        okButtonProps={{ danger: true }}
        cancelText="Go Back"
      >
        {selectedProfile && (
          <div>
            <div style={{ marginBottom: 16, padding: 12, background: "#f8f9fd", borderRadius: 6 }}>
              <p style={{ margin: 0, fontWeight: 500 }}>{selectedProfile.name}</p>
              <p style={{ margin: "4px 0 0", color: "#666", fontSize: 13 }}>
                {selectedProfile.jobTitle}
              </p>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                Reason for Cancellation *
              </label>
              <Input.TextArea
                rows={4}
                placeholder="Please provide a reason for cancelling this interview..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>
          </div>
        )}
      </Modal>
    </SubmittedProfilesWrapper>
  );
};

export default SubmittedProfiles;
